import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import { maintenanceApi } from "@/api/maintenanceApi"
import { MaintenanceForm } from "../components/MaintenanceForm"
import { FormPageLayout } from "@/shared/components/forms"
import { PageLoading } from "@/shared/components/ui/LoadingSpinner"
import { useFarmFromUrl } from "@/shared/hooks/useUrlSync"
import { pondsApi } from "@/features/ponds/api/pondsApi"
import { queryKeys } from "@/shared/api/queryClient"
import { buildRoute } from "@/shared/lib/constants"
import { toApiRequest, type MaintenanceFormData } from "../schemas/maintenanceSchema"

export function CreateMaintenancePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  const { farmId, isLoading: isFarmLoading } = useFarmFromUrl()
  const pondId = searchParams.get("pondId")

  // Fetch pond data using query params
  const { data: pond, isLoading: isPondLoading } = useQuery({
    queryKey: queryKeys.ponds.detail(farmId || "", pondId || ""),
    queryFn: () => pondsApi.get(farmId!, pondId!),
    enabled: !!farmId && !!pondId,
  })

  const createMutation = useMutation({
    mutationFn: (data: MaintenanceFormData) => {
      if (!farmId || !pondId) throw new Error("Farm and pond required")
      return maintenanceApi.create(farmId, pondId, toApiRequest(data))
    },
    onSuccess: () => {
      if (pondId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all(pondId) })
      }
      handleCancel()
    },
  })

  const handleCancel = () => {
    if (farmId) {
      navigate(buildRoute.maintenance(farmId, pondId))
    }
  }

  const handleSubmit = async (data: MaintenanceFormData) => {
    await createMutation.mutateAsync(data)
  }

  if (isFarmLoading || isPondLoading) {
    return <PageLoading message="Loading..." />
  }

  if (!farmId || !pondId) {
    return (
      <div className="text-center py-16">
        <h3 className="font-roboto text-base font-semibold text-gray-900">
          Pond selection required
        </h3>
        <p className="font-roboto text-sm text-gray-600 mt-2">
          Please select a pond to create a maintenance check.
        </p>
      </div>
    )
  }

  return (
    <FormPageLayout
      title="Add Maintenance Check"
      subtitle={pond?.name}
      onCancel={handleCancel}
      onSubmit={() => {
        document.getElementById("maintenance-form")?.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        )
      }}
      isSubmitting={createMutation.isPending}
      submitLabel="Create Check"
      cancelLabel="Cancel"
    >
      <MaintenanceForm
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending}
      />
    </FormPageLayout>
  )
}
